import { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useStudents } from '../hooks/useStudents';
import type { Student } from '../api/students';
import StudentItem from './StudentItem';
import StudentCard from './StudentCard';
import Spinner from './Spinner';
import { useSocket } from '../context/SocketProvider';
import { useAuth } from '../context/AuthProvider';
import DismissalStats from './DismissalStats';
import DismissalFilters, { type PickupTypeFilter, type WaitTimeFilter } from './DismissalFilters';
import SortMenu from './SortMenu';
import { filterStudents, sortStudents, getUniqueGrades, type SortOption } from '../utils/studentFilters';

export default function StudentList() {
  const { data, isLoading, error, refetch, isRefetching } = useStudents();
  const { user } = useAuth();
  const socketContext = useSocket();
  const isConnected = socketContext?.isConnected ?? false;
  const students: Student[] = data ?? [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Only staff and admin can dismiss students
  const canDismiss = user?.role === 'staff' || user?.role === 'admin';

  // Filter and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [pickupTypeFilter, setPickupTypeFilter] = useState<PickupTypeFilter>('all');
  const [waitTimeFilter, setWaitTimeFilter] = useState<WaitTimeFilter>('all');
  const [gradeFilter, setGradeFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('waitTime');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Get unique grades from all students
  const availableGrades = useMemo(() => getUniqueGrades(students), [students]);

  // Apply filters and sorting
  const filteredAndSortedStudents = useMemo(() => {
    const filtered = filterStudents(students, {
      searchQuery,
      pickupType: pickupTypeFilter,
      waitTime: waitTimeFilter,
      grade: gradeFilter,
    });
    return sortStudents(filtered, { sortBy, sortDirection });
  }, [students, searchQuery, pickupTypeFilter, waitTimeFilter, gradeFilter, sortBy, sortDirection]);

  // Check if any filters are active
  const hasActiveFilters =
    searchQuery !== '' ||
    pickupTypeFilter !== 'all' ||
    waitTimeFilter !== 'all' ||
    gradeFilter !== '';

  const handleClearAllFilters = () => {
    setSearchQuery('');
    setPickupTypeFilter('all');
    setWaitTimeFilter('all');
    setGradeFilter('');
  };

  const handleSortChange = (newSortBy: SortOption, newSortDirection: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 5 } }}>
      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={3}>
          {/* Header */}
          <Toolbar disableGutters sx={{
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            px: 0
          }}>
            <Box>
              <Typography
                variant="h4"
                fontWeight={600}
                sx={{
                  fontSize: { xs: '1.5rem', sm: '2rem' }
                }}
              >
                Today's Dismissals
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  mt: 0.5
                }}
              >
                {new Date().toLocaleDateString(undefined, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <SortMenu
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              />
              {isConnected ? (
                <Chip
                  icon={<CheckCircleIcon />}
                  label={isMobile ? 'Live' : 'Live Updates Active'}
                  size="small"
                  sx={(theme) => ({
                    backgroundColor: theme.palette.success.light + '30',
                    color: theme.palette.success.dark,
                    fontWeight: 500,
                    border: 'none',
                    '& .MuiChip-icon': {
                      color: theme.palette.success.light
                    }
                  })}
                />
              ) : (
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => refetch()}
                  disabled={isRefetching}
                >
                  {isRefetching ? 'Refreshing...' : (isMobile ? 'Refresh' : 'Refresh (Disconnected)')}
                </Button>
              )}
            </Stack>
          </Toolbar>

          {error && <Alert severity="error">Something went wrong. Please try again.</Alert>}

          {/* Stats Dashboard */}
          {!isLoading && students.length > 0 && (
            <DismissalStats students={students} />
          )}

          {/* Filters */}
          {!isLoading && students.length > 0 && (
            <DismissalFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              pickupTypeFilter={pickupTypeFilter}
              onPickupTypeChange={setPickupTypeFilter}
              waitTimeFilter={waitTimeFilter}
              onWaitTimeChange={setWaitTimeFilter}
              gradeFilter={gradeFilter}
              onGradeFilterChange={setGradeFilter}
              availableGrades={availableGrades}
              onClearAll={handleClearAllFilters}
              hasActiveFilters={hasActiveFilters}
            />
          )}

          {isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <Spinner />
            </Box>
          ) : (
            <>
              {/* Results Summary */}
              {hasActiveFilters && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {filteredAndSortedStudents.length} of {students.length} students
                  </Typography>
                </Box>
              )}

              {/* Desktop Table View */}
              {!isMobile && (
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Grade</TableCell>
                        <TableCell>Pickup</TableCell>
                        <TableCell>Checked In</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAndSortedStudents.map((student) => (
                        <StudentItem key={student.id} student={student} onDismissed={refetch} canDismiss={canDismiss} />
                      ))}
                      {filteredAndSortedStudents.length === 0 && students.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            No students match the current filters.
                          </TableCell>
                        </TableRow>
                      )}
                      {students.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            No students have been checked in yet today.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Mobile Card View */}
              {isMobile && (
                <Stack spacing={2}>
                  {students.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                      <Typography variant="body1" color="text.secondary">
                        No students have been checked in yet today.
                      </Typography>
                    </Paper>
                  ) : filteredAndSortedStudents.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                      <Typography variant="body1" color="text.secondary">
                        No students match the current filters.
                      </Typography>
                    </Paper>
                  ) : (
                    filteredAndSortedStudents.map((student) => (
                      <StudentCard key={student.id} student={student} onDismissed={refetch} canDismiss={canDismiss} />
                    ))
                  )}
                </Stack>
              )}
            </>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
